/**
 * Lane C0-PROPS: the market's front-row stalls (C0_TOWN_LAYOUT §2 row 6: ≤ 40 px so shoppers on the street stay
 * visible): a counter or mats under a low cloth awning, goods by what the stall is (the layout's name): the notice
 * board, the kitchen, the grocer and the player stalls (bare mats and tables for players to lay out).
 */
import { hash2 } from '@nine/shared';
import { box, M, snap, wall } from '../buildings';
import type { Sprite } from '../scenery';
import type { Stage } from './common';
import { awning, blob, done, K, stage } from './parts';

const wood = (u: number, v: number, l: number) => snap(M.wood, l + ((u | 0) % 7 === 0 ? -0.2 : 0), u | 0, v | 0);
const top = (u: number, v: number, l: number) => snap(M.deck, l + 0.1 + ((u | 0) % 6 === 0 ? -0.15 : 0), u | 0, v | 0, 0.2);

/** A counter along x from x0 to x1 at depth y0..y1, 16 px high. */
function counter(s: Stage, x0: number, x1: number, y0: number, y1: number, z = 16): void {
  box(s.px, s.P, [x0, y0, x1, y1], 0, z, wood, top);
}

/** Goods in a row on a counter top: `kind` picks the look. */
function goods(s: Stage, x0: number, x1: number, y: number, z: number, kind: 'produce' | 'food' | 'cloth' | 'pots', seed: number): void {
  const n = Math.max(2, Math.round((x1 - x0) * 2.2));
  for (let k = 0; k < n; k++) {
    const x = x0 + 0.25 + ((x1 - x0 - 0.5) * k) / (n - 1);
    const [gx, gy] = s.P(x, y, z);
    const h = hash2(k + seed * 31, seed);
    if (kind === 'produce') {
      blob(s.px, gx, gy - 2, 4, 2.5, K.straw, 0.5);
      blob(s.px, gx, gy - 4, 3, 1.6, [M.red, M.jade, K.ochre, M.terra][Math.floor(h * 4)]!, 0.65);
    } else if (kind === 'food') {
      // Steamer baskets stacked.
      for (let q = 0; q < 2 + Math.floor(h * 2); q++) blob(s.px, gx, gy - 2 - q * 3, 4, 2, K.straw, 0.6 - q * 0.05);
    } else if (kind === 'cloth') blob(s.px, gx, gy - 2, 4, 2, [M.red, K.indigo, K.ochre, M.jade, K.cream][Math.floor(h * 5)]!, 0.6);
    else blob(s.px, gx, gy - 4, 3, 4, h < 0.5 ? M.terra : M.blue, 0.55);
  }
}

/** Things hung under an awning's front edge along x from x0 to x1 at depth y, height z: lanterns, garlic and chilli
 * strings, dried fish, gourds, little banners (round 3: denser bays). */
function hangers(s: Stage, x0: number, x1: number, y: number, z: number, seed: number): void {
  const n = Math.max(3, Math.round((x1 - x0) * 3));
  for (let k = 0; k < n; k++) {
    const [x, yy] = s.P(x0 + 0.15 + ((x1 - x0 - 0.3) * k) / (n - 1), y, z);
    const kind = Math.floor(hash2(k + seed * 7, seed) * 5);
    s.px.rect(x, yy, 1, 2, M.dark[2]!);
    if (kind === 0) {
      blob(s.px, x, yy + 5, 2.5, 3, M.red, 0.65);
      s.px.rect(x - 1, yy + 1, 3, 1, M.gold[3]!);
    } else if (kind === 1) for (let t = 0; t < 4; t++) blob(s.px, x, yy + 3 + t * 2, 1.5, 1, K.cream, 0.7);
    else if (kind === 2) for (let t = 0; t < 5; t++) s.px.rect(x - (t & 1), yy + 2 + t * 2, 2, 2, M.red[2 + (t & 1)]!);
    else if (kind === 3) {
      s.px.rect(x - 1, yy + 2, 2, 8, K.iron[3]!);
      s.px.rect(x - 2, yy + 9, 4, 1, K.iron[2]!);
    } else blob(s.px, x, yy + 5, 2, 3, K.ochre, 0.6);
  }
}

/** A stall by its name: the notice board, the kitchen, the grocer or a player stall. `w` × `h` tiles. */
export function stall(w: number, h: number, name: string, v: number): Sprite {
  const s = stage(w, h, 48, 22);
  const front = h - 1.2;
  if (name === 'ประกาศ') {
    // The notice board: a framed board of papers under a small roof, two posts, a bench.
    const x0 = 0.8;
    const x1 = w - 0.8;
    for (const x of [x0, x1]) box(s.px, s.P, [x - 0.1, front - 0.1, x + 0.1, front + 0.1], 0, 36, wood);
    wall(s.px, s.P, 'left', front, x0 + 0.1, x1 - 0.1, 10, 32, (u, vv) => {
      const c = Math.floor(u / 9) + Math.floor(vv / 8) * 7;
      if ((u | 0) % 9 === 0 || (vv | 0) % 8 === 0) return snap(M.wood, 0.45, u | 0, vv | 0, 0);
      const paper = hash2(c, 3) < 0.75;
      return paper ? snap(hash2(c, 5) < 0.2 ? M.red : K.cream, 0.6 + ((u | 0) % 3 === 0 && (vv | 0) % 2 ? -0.35 : 0), u | 0, vv | 0, 0) : snap(M.wood, 0.55, u | 0, vv | 0, 0);
    });
    awning(s.px, s.P, 'left', front - 0.2, x0 - 0.3, x1 + 0.3, 40, 0.6, 6, M.red, M.red);
    box(s.px, s.P, [1.5, front + 0.7, w - 1.5, front + 1.0], 0, 8, wood, top);
    return done(s);
  }
  if (name === 'ครัว') {
    // The kitchen: a brick stove with a wok and steam, a counter of steamers, stools.
    box(s.px, s.P, [0.6, 0.8, 2.2, 2.0], 0, 18, (u, vv, l) => snap(M.brick, l, u | 0, vv | 0), (u, vv) => snap(M.dark, 0.4, u | 0, vv | 0));
    const [wx, wy] = s.P(1.4, 1.4, 18);
    blob(s.px, wx, wy, 6, 3, K.iron, 0.4);
    const [fx, fy] = s.P(2.2, 1.4, 6);
    s.px.rect(fx - 2, fy - 2, 4, 3, M.red[4]!);
    counter(s, 2.6, w - 0.6, front - 0.6, front, 16);
    goods(s, 2.6, w - 0.6, front - 0.3, 16, 'food', v);
    for (const x of [3.2, w - 1.4]) box(s.px, s.P, [x - 0.15, front + 0.5, x + 0.15, front + 0.8], 0, 8, wood, top);
    awning(s.px, s.P, 'left', front - 1.9, 2.4, w - 0.4, 40, 1.5, 8, K.indigo, K.cream, true);
    hangers(s, 2.6, w - 0.6, front - 0.45, 31, v + 1);
    return done(s);
  }
  if (name === 'ของชำ') {
    counter(s, 0.6, w - 0.6, front - 0.6, front, 14);
    goods(s, 0.6, w - 0.6, front - 0.3, 14, 'produce', v);
    // Baskets and jars on the ground in front.
    for (let k = 0; k < 3; k++) {
      const [bx, by] = s.P(1 + k * 1.6, front + 0.6, 0);
      blob(s.px, bx, by - 4, 5, 4, k === 1 ? M.terra : K.straw, 0.5);
      blob(s.px, bx, by - 8, 4, 1.5, [M.red, K.ochre, M.jade][k]!, 0.7);
    }
    awning(s.px, s.P, 'left', front - 1.9, 0.4, w - 0.4, 40, 1.5, 8, K.ochre, K.cream, true);
    hangers(s, 0.6, w - 0.6, front - 0.45, 31, v + 2);
    return done(s);
  }
  // A player stall: low tables and rugs in bays, each under its own awning.
  const bays = Math.max(1, Math.round(w / 4.5));
  const bw = (w - 0.6) / bays;
  for (let b = 0; b < bays; b++) {
    const x0 = 0.3 + b * bw + 0.25;
    const x1 = 0.3 + (b + 1) * bw - 0.25;
    // The rug, then a low table on it.
    box(s.px, s.P, [x0, front - 0.8, x1, front + 0.6], 0, 1, () => null, (u, vv) => {
      const edge = (u | 0) % 12 < 2 || (vv | 0) % 12 < 2;
      return snap(edge ? M.gold : [M.red, K.indigo, M.jade][(b + v) % 3]!, 0.5 + ((u + vv) % 4 < 2 ? 0.05 : -0.05), u | 0, vv | 0, 0);
    });
    counter(s, x0 + 0.3, x1 - 0.3, front - 0.6, front - 0.1, 10);
    goods(s, x0 + 0.3, x1 - 0.3, front - 0.35, 10, (['cloth', 'pots', 'produce'] as const)[(b + v) % 3]!, v * 7 + b);
    awning(s.px, s.P, 'left', front - 1.9, x0, x1, 40, 1.3, 8, [M.red, K.cream, K.indigo][(b + v) % 3]!, K.cream, true);
    hangers(s, x0 + 0.1, x1 - 0.1, front - 0.65, 31, v * 3 + b);
    // A sign on the bay's corner pole and crates at its end.
    box(s.px, s.P, [x1 - 0.12, front - 0.75, x1 + 0.04, front - 0.6], 18, 30, (u, vv, l) => snap(M.red, l + 0.2 + ((vv | 0) % 4 === 0 ? 0.2 : 0), u | 0, vv | 0, 0));
    box(s.px, s.P, [x0 - 0.05, front + 0.3, x0 + 0.35, front + 0.65], 0, 8, (u, vv, l) => snap(M.deck, l + ((u | 0) % 6 === 0 ? -0.25 : 0), u | 0, vv | 0, 0));
  }
  return done(s);
}
